import type { AnimationEvent } from '@angular/animations';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  isPlatformBrowser,
  NgComponentOutlet,
  NgSwitch,
  NgSwitchCase,
} from '@angular/common';
import type { OnDestroy, OnInit, ViewRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  PLATFORM_ID,
  Renderer2,
  inject,
} from '@angular/core';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { TimeUtils } from '@freelancer/time-utils';
import { TrackingModule } from '@freelancer/tracking';
import { ButtonComponent } from '@freelancer/ui/button';
import { ButtonColor } from '@freelancer/ui/button';
import { IconModule } from '@freelancer/ui/icon';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkColor, LinkHoverColor, LinkUnderline } from '@freelancer/ui/link';
import { SpinnerComponent } from '@freelancer/ui/spinner';
import { SpinnerColor, SpinnerSize } from '@freelancer/ui/spinner';
import { FontColor } from '@freelancer/ui/text';
import { Subject, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { ToastAlertService } from './toast-alert.service';
import type { ToastAlertComponentInterface } from './toast-alert.types';
import {
  CustomComponentConfig,
  DynamicToastAlertAction,
  GenericError,
  ToastAlertColor,
  ToastAlertType,
} from './toast-alert.types';
import { GenericErrorComponent } from '../generic-error/generic-error.component';
import { LinkModule } from '../link/link.module';

@Component({
  standalone: true,
  imports: [
    ButtonComponent,
    GenericErrorComponent,
    IconModule,
    LinkModule,
    NgComponentOutlet,
    NgSwitch,
    NgSwitchCase,
    SpinnerComponent,
    TrackingModule,
  ],
  template: `
    <div
      class="ToastAlert"
      [attr.data-type]="type"
      [attr.data-color]="color"
      [@toggleElement]="state"
      (@toggleElement.done)="handleAnimationDone($event)"
      (mouseenter)="handleMouseEnter()"
      (mouseleave)="handleMouseLeave()"
    >
      <ng-container [ngSwitch]="type">
        <fl-icon
          class="ToastAlert-icon mr-xsmall"
          *ngSwitchCase="ToastAlertType.INFO"
          [color]="IconColor.INHERIT"
          [fillIconFont]="false"
          [name]="'ui-info'"
          [size]="IconSize.MID"
          [useIconFont]="true"
        ></fl-icon>
        <fl-icon
          class="ToastAlert-icon mr-xsmall"
          *ngSwitchCase="ToastAlertType.ERROR"
          [color]="IconColor.INHERIT"
          [fillIconFont]="false"
          [name]="'ui-error'"
          [size]="IconSize.MID"
          [useIconFont]="true"
        ></fl-icon>
        <fl-icon
          class="ToastAlert-icon mr-xsmall"
          *ngSwitchCase="ToastAlertType.SUCCESS"
          [color]="IconColor.INHERIT"
          [fillIconFont]="false"
          [size]="IconSize.MID"
          [name]="'ui-check-in-circle-v2'"
          [useIconFont]="true"
        ></fl-icon>
        <fl-icon
          class="ToastAlert-icon mr-xsmall"
          *ngSwitchCase="ToastAlertType.WARNING"
          [color]="IconColor.INHERIT"
          [fillIconFont]="false"
          [name]="'ui-warning'"
          [size]="IconSize.MID"
          [useIconFont]="true"
        ></fl-icon>
        <fl-spinner
          class="ToastAlert-loader mr-xsmall"
          *ngSwitchCase="ToastAlertType.LOADING"
          [size]="SpinnerSize.SMALL"
          [color]="
            color === ToastAlertColor.LIGHT
              ? SpinnerColor.GRAY
              : SpinnerColor.LIGHT
          "
        ></fl-spinner>
      </ng-container>
      <div
        class="ToastAlert-content text-xsmall font-normal {{
          color === ToastAlertColor.LIGHT ? 'text-foreground' : 'text-light'
        }} {{ closeable ? 'mr-xsmall' : 'mr-none' }}"
        [attr.data-closeable]="closeable"
      >
        @if (content && !error) {
          {{ content }}
          @if (action) {
            @if (action.type === 'link') {
              <fl-link
                class="ToastAlert-action"
                [link]="action.url"
                [queryParams]="action.queryParams"
                [color]="action.color || LinkColor.DEFAULT"
                [hoverColor]="action.hoverColor || LinkHoverColor.DEFAULT"
                [underline]="action.underline || LinkUnderline.HOVER"
                [flTrackingLabel]="action.trackingLabel"
                [flTrackingSection]="action.trackingSection"
              >
                {{ action.text }}
              </fl-link>
            }
            @if (action.type === 'button') {
              <fl-button
                class="ToastAlert-action"
                [color]="action.color || ButtonColor.TEXT_BLUE"
                [flTrackingLabel]="action.trackingLabel"
                [flTrackingSection]="action.trackingSection"
                (click)="action.onClick()"
              >
                {{ action.text }}
              </fl-button>
            }
          }
        } @else {
          @if (error) {
            @if (error.retry) {
              <fl-generic-error
                [flTrackingSection]="'ToastAlertGenericError'"
                [error]="error.error"
                [fontColor]="fontColor ? fontColor : FontColor.INHERIT"
                (retry)="error.retry()"
              >
                {{ error.message }}
              </fl-generic-error>
            } @else {
              <fl-generic-error
                [flTrackingSection]="'ToastAlertGenericError'"
                [error]="error.error"
                [fontColor]="fontColor ? fontColor : FontColor.INHERIT"
              >
                {{ error.message }}
              </fl-generic-error>
            }
          } @else {
            @if (customComponentConfig?.component) {
              <ng-container
                *ngComponentOutlet="
                  customComponentConfig?.component;
                  inputs: customComponentConfig?.inputs
                "
              ></ng-container>
            } @else {
              <ng-content></ng-content>
            }
          }
        }
      </div>
      @if (closeable || toastAlertService.isAutoCloseDisabled()) {
        <fl-button
          class="ToastAlert-closeBtn"
          aria-label="Close toast alert"
          i18n-aria-label="Close button for toast alert"
          (click)="handleClose()"
        >
          <fl-icon
            [color]="
              color === ToastAlertColor.LIGHT
                ? IconColor.FOREGROUND
                : IconColor.LIGHT
            "
            [name]="'ui-close'"
            [size]="IconSize.MID"
            [useIconFont]="true"
          ></fl-icon>
        </fl-button>
      }
    </div>
  `,
  styleUrls: ['./toast-alert.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('toggleElement', [
      state(
        'visible',
        style({
          opacity: '1',
          transform: 'translateY(0)',
        }),
      ),
      state(
        'hidden',
        style({
          opacity: '0',
          transform: 'translateY(-12px)',
        }),
      ),
      transition(
        'visible => hidden',
        animate('200ms cubic-bezier(0.25, 0.8, 0.25, 1)'),
      ),
      transition(
        '* => visible',
        animate('200ms cubic-bezier(0.25, 0.8, 0.25, 1)'),
      ),
    ]),
  ],
})
export class ToastAlertComponent
  implements ToastAlertComponentInterface, OnInit, OnDestroy
{
  toastAlertService = inject(ToastAlertService);
  private platformId = inject(PLATFORM_ID);
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private timeUtils = inject(TimeUtils);

  ButtonColor = ButtonColor;
  FontColor = FontColor;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkColor = LinkColor;
  LinkHoverColor = LinkHoverColor;
  LinkUnderline = LinkUnderline;
  SpinnerColor = SpinnerColor;
  SpinnerSize = SpinnerSize;
  ToastAlertColor = ToastAlertColor;
  ToastAlertType = ToastAlertType;

  element: HTMLElement;
  state: string;

  @RepetitiveSubscription()
  private timer?: Subscription;
  private resetTimerSubject$: Subject<undefined> = new Subject();

  @Input() closeable = false;
  @Input() color = ToastAlertColor.LIGHT;
  @Input() id: string;
  @Input() timeout? = 3000;
  @Input() type = ToastAlertType.INFO;

  /**
   * Only for use with dynamic toast alerts. (.open instead of .openById).
   */
  @Input() content?: string;
  /**
   * Only for use with dynamic toast alerts. (.open instead of .openById).
   */
  @Input() action?: DynamicToastAlertAction;
  /**
   * Only for use with dynamic toast alerts. (.open instead of .openById).
   */
  @Input() error?: GenericError;
  /**
   * Only for use with dynamic toast alerts. (.open instead of .openById).
   */
  @Input() fontColor?: FontColor;
  /**
   * Only for use with openComponent().
   */
  @Input() customComponentConfig?: CustomComponentConfig;
  /**
   * If true, the toast alert will persist when the user hovers over it.
   * Only for use with dynamic toast alerts. (.open instead of .openById).
   */
  @Input() persistWhenHover?: boolean;

  @Output() close = new EventEmitter();

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.id) {
      console.error('Toast alert must have an id');
      return;
    }

    this.element = this.el.nativeElement;
    this.toastAlertService.add(this, this.id);
    this.state = 'hidden';
  }

  handleMouseEnter(): void {
    if (this.persistWhenHover) {
      this.unsubscribeTimer();
    }
  }

  handleMouseLeave(): void {
    if (this.persistWhenHover) {
      this.startTimer();
    }
  }

  handleClose(): void {
    this.toastAlertService.close(this.id);
    this.close.emit();
  }

  move(position: number): void {
    this.renderer.setStyle(this.element, 'top', `${position}px`);
  }

  startTimer(): void {
    if (this.timeout && !this.toastAlertService.isAutoCloseDisabled()) {
      this.timer = this.resetTimerSubject$
        .pipe(startWith(undefined), this.timeUtils.rxDebounceTime(this.timeout))
        .subscribe(() => this.toastAlertService.close(this.id));
    }
  }

  resetTimer(): void {
    this.resetTimerSubject$.next(undefined);
  }

  unsubscribeTimer(): void {
    if (this.timer) {
      this.timer.unsubscribe();
    }
  }

  toggleVisibility(visibility: string): void {
    this.state = visibility;

    if (!(this.changeDetectorRef as ViewRef).destroyed) {
      this.changeDetectorRef.markForCheck();
    }
  }

  handleAnimationDone(event: AnimationEvent): void {
    if (event.fromState === 'visible') {
      this.unsubscribeTimer();
      this.toastAlertService.removeElement(this.id);
    }
  }

  ngOnDestroy(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.toastAlertService.close(this.id);
    this.toastAlertService.removeElement(this.id);
    this.toastAlertService.remove(this.id);
    this.unsubscribeTimer();
  }
}
